import { getFirstCapturingGroup } from './regex';

const { isString } = require('./type');

const urlRegexs = {
  buildCaptureQueryStringFieldValue: (param) =>
    new RegExp(`[?&]?${encodeURIComponent(param)}=([^&]*)`),
  captureSlug: /.*\/([\w-]+)\/?(?:\?.*)?$/,
};

/**
 * Returns true if the given url is absolute. Otherwise, false.
 * @param {string} url url to check
 * @returns {boolean} true if the url is absolute
 */
const isAbsoluteUrl = (url) => url.indexOf('http') === 0;

/**
 * Returns true if the given url is relative. Otherwise, false.
 * @param {string} url url to check
 * @returns {boolean} true if the url is relative
 */
const isRelativeUrl = (url) => !isAbsoluteUrl(url);

/**
 * Factory function for an URL object
 * @param {string|URL} url string or URL
 * @returns {URL} URL object
 */
const createURL = (url) => {
  let result = url;
  if (isString(url)) {
    result = isRelativeUrl(url)
      ? new URL(url, window.location.origin)
      : new URL(url);
  }
  return result;
};

/**
 * Get the value of a param from an url
 * @param {string} url source url
 * @param {string} field param name
 * @returns {string} slug
 */
const getQueryStringFieldValueFromUrl = (url, field) =>
  getFirstCapturingGroup(
    urlRegexs.buildCaptureQueryStringFieldValue(field),
    url,
  );

/**
 * Get the slug of an url
 * @param {string} url source url
 * @returns {string} slug
 */
const getSlugFromUrl = (url) =>
  getFirstCapturingGroup(urlRegexs.captureSlug, url);

/**
 * Parse an url to a absolute url
 * @param {string|URL} url url to parse
 * @returns {string} absolute url
 */
const parseAbsoluteUrl = (url) => {
  const urlObj = createURL(url);
  return urlObj.toString();
};

/**
 * Parse an url to a relative url
 * @param {string|URL} url url to parse
 * @returns {string} relative url
 */
const parseRelativeUrl = (url) => {
  const urlObj = createURL(url);
  return urlObj.pathname + urlObj.search + urlObj.hash;
};

/**
 * Returns a new url after adding or modifing its search params with the given
 * object (param/value pairs).
 *
 * @param {string} url URL (absolute or relative)
 * @param {object} params param/value pairs to set
 * @returns {string}  Modified url
 */
function urlWithSearchParams(url, params) {
  const isRelative = isRelativeUrl(url);
  const urlObj = createURL(url);
  Object.keys(params).forEach((param) =>
    urlObj.searchParams.set(param, params[param]));
  return isRelative
    ? parseRelativeUrl(urlObj) // Return a relative url as the funtion received
    : parseAbsoluteUrl(urlObj); // Return an absolute url
}

/**
 * Returns a new url with the given URLSearchParams
 * Ignores existing paramas in the url
 *
 * @param {URLSearchParams} urlSearchParams Url search params
 * @returns {string}  Modified url
 */
function urlWithURLSearchParams(urlSearchParams) {
  return `${window.location.href.split('?')[0]}?${urlSearchParams.toString()}`;
}

/**
 * Returns a new url after removing all its search params
 *
 * @param {string} url URL (absolute or relative)
 * @returns {string}  Modified url
 */
function urlWithoutParams(url) {
  return url.split('?')[0];
}

/** Remove every url parameter from the current URL
 */
function removeCurrentUrlParameters() {
  const currentURL = window.location.href;
  window.history.replaceState({}, document.title, urlWithoutParams(currentURL));
}

/**
 * Removes url param by name is URLSearchParams object
 * @param {string} paramName param name
 * @return {URLSearchParams} new urlSearchParam
 */
function removeUrlParam(paramName) {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete(paramName);
  return urlParams;
}

export {
  getQueryStringFieldValueFromUrl,
  getSlugFromUrl,
  isAbsoluteUrl,
  isRelativeUrl,
  parseAbsoluteUrl,
  parseRelativeUrl,
  removeCurrentUrlParameters,
  removeUrlParam,
  urlWithoutParams,
  urlWithSearchParams,
  urlWithURLSearchParams,
};
